<template>
  <li
    class="streak-item"
    :class="{ 'streak-item-active': active }"
    :style="{
      width: $isMobile() ? '100%' : '',
      backgroundColor: resolvedBackgroundColor,
      color: resolvedTextColor,
    }"
    role="button"
    tabindex="0"
    :aria-pressed="active ? 'true' : 'false'"
    :aria-label="resolvedAriaLabel"
    @click="$emit('click')"
    @keydown.enter.prevent="$emit('click')"
    @keydown.space.prevent="$emit('click')"
  >
    <div class="content">
      <span class="counter">{{ counter }}</span>
      <div class="text-container">
        <span class="title">{{ title }}</span>
        <span v-if="subtitle" class="subtitle">{{ subtitle }}</span>
      </div>
    </div>
  </li>
</template>

<script>
export default {
  name: "W4MstreakElement",
  props: {
    title: {
      type: String,
      default: "",
    },
    backgroudColor: {
      type: String,
      default: "",
    },
    backgroundColor: {
      type: String,
      default: "",
    },
    textColor: {
      type: String,
      default: "#ffffff",
    },
    active: {
      type: Boolean,
      default: false,
    },
    counter: {
      type: [Number, String],
      default: 0,
    },
    subtitle: {
      type: String,
      default: "",
    },
    ariaLabel: {
      type: String,
      default: "",
    },
  },
  computed: {
    resolvedBackgroundColor() {
      return this.backgroundColor || this.backgroudColor || "#3f4a5a";
    },
    resolvedTextColor() {
      return this.textColor || "#ffffff";
    },
    resolvedAriaLabel() {
      if (this.ariaLabel) {
        return this.ariaLabel;
      }
      const subtitleText = this.subtitle ? `, ${this.subtitle}` : "";
      return `${this.title}: ${this.counter}${subtitleText}`;
    },
  },
};
</script>

<style scoped>
.streak-item {
  display: flex !important;
  align-items: center;
  min-height: 50px;
  padding: 8px 15px 8px 25px; /* Left padding accommodates the chevron notch */
  position: relative;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  cursor: pointer;
  outline: none;
  /* The background color is set dynamically via style binding */
}

/* Chevron Shapes */
.streak-item {
  clip-path: polygon(
    0% 0%,
    calc(100% - 15px) 0%,
    100% 50%,
    calc(100% - 15px) 100%,
    0% 100%,
    15px 50%
  );
  /* Fallback or standard shape */
  margin-right: -15px; /* Overlap to fit the puzzle piece together */
  z-index: 1;
}

.streak-item:first-child {
  clip-path: polygon(
    0% 0%,
    calc(100% - 15px) 0%,
    100% 50%,
    calc(100% - 15px) 100%,
    0% 100%
  );
  padding-left: 15px; /* Less padding since no notch */
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
}

.streak-item:last-child {
  clip-path: polygon(
    0% 0%,
    100% 0%,
    100% 100%,
    0% 100%,
    15px 50%
  );
  margin-right: 0;
  padding-right: 15px;
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
}

/* Active State styling */
.streak-item.streak-item-active {
  z-index: 10;
  filter: brightness(1.1);
  /* Removed transform: scale(1.05) to keep alignment consistent */
  font-weight: bold; /* Optional: bold text to indicate selection */
}

.streak-item:focus-visible {
  z-index: 15;
  box-shadow: 0 0 0 2px #ffffff, 0 0 0 4px rgba(28, 116, 255, 0.65);
}

.content {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.counter {
  font-weight: 800;
  font-size: 1.2rem;
  margin-right: 8px;
  opacity: 0.9;
}

.text-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  line-height: 1.1;
}

.title {
  font-weight: 600;
  font-size: 0.95rem;
  white-space: nowrap;
  text-transform: uppercase;
}

.subtitle {
  font-size: 0.75rem;
  opacity: 0.85;
}

/* Mobile adjustments */
@media screen and (max-width: 768px) {
  .streak-item {
    min-height: 48px;
    margin-bottom: 8px;
    clip-path: none !important; /* Remove complicated shapes on mobile for simpler stacking */
    border-radius: 6px;
    margin-right: 0;
    padding: 10px;
  }
}
</style>
